import storageService from "@/services/storage.service";
import axios from "axios";
import router from "../router";
import { startLoader, stopLoader } from "@/services/loader";

const getBaseUrl = () => process.env.API_BASE_URL || "http://127.0.0.1:8000";

const AUTH_ENDPOINTS = [
  "/accounts/login/",
  "/accounts/otp_send/",
  "/accounts/token/refresh/",
  "/api/token/refresh/",
];

// Requests that must never show the blocking loader (background / housekeeping).
const NO_LOADER_URLS = ["notif", "token/refresh", "logout", "status_options"];

// Friendly messages for the actions people wait on.
const LOADER_MESSAGES = [
  ["otp_send", "Sending OTP..."],
  ["add_member", "Adding staff member..."],
  ["initial_application_submit", "Setting up your application..."],
  ["/accounts/login/", "Signing you in..."],
];

function wantsLoader(config) {
  if (config.loader === false) return false;
  if (config.loader === true) return true;
  const url = String(config.url || "").toLowerCase();
  return !NO_LOADER_URLS.some((part) => url.includes(part));
}

function loaderMessage(config) {
  const url = String(config.url || "").toLowerCase();
  const match = LOADER_MESSAGES.find(([part]) => url.includes(part));
  if (match) return match[1];
  const method = String(config.method || "get").toLowerCase();
  return method === "get" ? "Loading..." : "Please wait...";
}

// Page data (GET) is only covered if it is actually slow, so quick loads never flash.
// Saves (POST/PUT/...) use the default delay.
const GET_LOADER_DELAY = 300;
function loaderDelay(config) {
  return String(config.method || "get").toLowerCase() === "get"
    ? GET_LOADER_DELAY
    : undefined;
}

/**
 * True only for requests that go to OUR API (the configured base URL, or any
 * relative URL, which resolves against it). The Bearer token and cookies must
 * never be attached to third-party hosts, and a third-party 401 must never
 * trigger our token-refresh flow.
 */
function isApiRequest(config, instance) {
  const rawUrl = String((config && config.url) || "");
  const base =
    (config && config.baseURL) ||
    (instance && instance.defaults && instance.defaults.baseURL) ||
    getBaseUrl() ||
    window.location.origin;
  try {
    const apiOrigin = new URL(base, window.location.origin).origin;
    return new URL(rawUrl, new URL(base, window.location.origin)).origin === apiOrigin;
  } catch {
    return false;
  }
}

export function installAuthInterceptors(instance) {
  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  instance.interceptors.request.use(
    (config) => {
      if (!isApiRequest(config, instance)) {
        return config;
      }
      config.withCredentials = true;
      if (!config.__loaderId && wantsLoader(config)) {
        config.__loaderId = startLoader({
          message: loaderMessage(config),
          delay: loaderDelay(config),
        });
      }
      const token = storageService.getItem("accessToken");
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
        if (config.params) {
          if (config.params.token) delete config.params.token;
          if (config.params.access_token) delete config.params.access_token;
        }
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => {
      stopLoader(response.config && response.config.__loaderId);
      return response;
    },
    async (error) => {
      if (error.config) {
        // a retried request (after token refresh) starts its own loader
        stopLoader(error.config.__loaderId);
        error.config.__loaderId = null;
      }
      const originalRequest = error.config;
      if (!originalRequest || !isApiRequest(originalRequest, instance)) {
        return Promise.reject(error);
      }

      const requestUrl = (originalRequest.url || "").toLowerCase();
      const isAuthEndpoint = AUTH_ENDPOINTS.some((p) => requestUrl.includes(p));

      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry &&
        !isAuthEndpoint
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const baseURL = (instance.defaults.baseURL || getBaseUrl() || "").replace(
          /\/+$/,
          "",
        );
        const refreshUrl = `${baseURL}/accounts/token/refresh/`;

        try {
          // The refresh token lives in the HttpOnly cookie; JS never sees it.
          const res = await axios.post(refreshUrl, {}, {
            withCredentials: true,
            headers: { "X-Requested-With": "XMLHttpRequest" },
            _retry: true,
          });

          if (res.data && (res.data.access || res.data.token)) {
            const newAccessToken = res.data.access || res.data.token;
            storageService.setItem("accessToken", newAccessToken);
            instance.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${newAccessToken}`;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            processQueue(null, newAccessToken);
            return instance(originalRequest);
          }
        } catch (refreshErr) {
          processQueue(refreshErr, null);
          const userType = storageService.getItem("userType");
          storageService.removeItem("accessToken");
          storageService.removeItem("refreshToken"); // purge legacy value
          storageService.removeItem("userType");

          const currentPath = router.currentRoute?.value?.path || "";
          if (!currentPath.includes("login") && !currentPath.includes("signup")) {
            router.push(userType === "applicant" ? "/login" : "/staff_login");
          }
          return Promise.reject(refreshErr);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

export async function revokeSession() {
  const baseURL = (axios.defaults.baseURL || getBaseUrl() || "").replace(/\/+$/, "");
  try {
    await axios.post(
      `${baseURL}/accounts/logout/`,
      {},
      {
        withCredentials: true,
        timeout: 8000,
        _retry: true,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      },
    );
  } catch {
    return false;
  }
  return true;
}

const apiClient = installAuthInterceptors(
  axios.create({
    baseURL: getBaseUrl(),
    timeout: 30000,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  }),
);

export default apiClient;